import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import OiButton from '../Common/OiButton/OiButton';

import FormTextarea from '../form-textarea/form-textarea';
import OiFormInput from '../OiForm/OiFormInput';
import DisplayImage from '../upload/custom-upload';

import { getCountry, getState, setProcessLoading } from '../../../store/action';

import {
    updateMerchantDetails,
    getAllbusinessTypes,
    uploadMerchantLogo
} from '../../../modules/merchants/store/actions/action.creators';

import { authService } from '../../../modules/users/services/dependency.injection';

const BusinessProfile = props => {
    const dispatch = useDispatch();

    const { merchantProfile, businessTypes } = useSelector(({ Merchants }) => Merchants);
    const { country, allStates, processLoading: loading } = useSelector(({ Util }) => Util);

    const [state, setState] = useState({
        merchantProfile: {
            merchant_id: '',
            image: null,
            imagePreview: '',
            merchantCategoryId: '',
            businessDescription: '',
            businessName: '',
            businessTypeId: '',
            location: {
                city: '',
                countryId: '',
                currencyCode: 'NGN',
                postalCode: '',
                stateId: '',
                streetAddress: ''
            }
        },
        validate: {
            businessEmail: '',
            businessPhone: ''
        },
        businessTypes: [],
        errors: {},
        image: ''
    });

    const saveLogoToLocalStorage = imagePreview => {
        localStorage.setItem('merchantLogo', imagePreview);
    };

    const setProfile = useCallback(() => {
        if (!merchantProfile || !merchantProfile.id) return;

        const { id, businessType, businessDescription, businessPhone, businessEmail, location, businessName, logoUrl } =
            merchantProfile;

        setState(prev => ({
            ...prev,
            merchantProfile: {
                ...prev.merchantProfile,
                merchant_id: id,
                businessTypeId: businessType?.id || '',
                businessName: businessName || '',
                businessDescription: businessDescription || '',
                location: location || {
                    city: '',
                    countryId: '',
                    currencyCode: 'NGN',
                    postalCode: '',
                    stateId: '',
                    streetAddress: ''
                },

                imagePreview: prev.merchantProfile.imagePreview || ''
            },
            validate: {
                ...prev.validate,
                businessEmail: businessEmail || '',
                businessPhone: businessPhone || ''
            }
        }));
    }, [merchantProfile]);

    // console.log(state);

    useEffect(() => {
        dispatch(getAllbusinessTypes());
        dispatch(getCountry());

        if (!merchantProfile?.id) return;

        setProfile();

        const cachedLogo = localStorage.getItem('merchantLogo');
        if (cachedLogo) {
            setState(prev => ({
                ...prev,
                merchantProfile: { ...prev.merchantProfile, imagePreview: cachedLogo }
            }));
        }

        const fetchSecureLogo = async () => {
            if (!merchantProfile.logoUrl) return;

            try {
                const token = authService.accessToken();
                const response = await fetch(merchantProfile.logoUrl, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (!response.ok) throw new Error('Failed to fetch logo');

                const blob = await response.blob();
                const secureImage = URL.createObjectURL(blob);

                setState(prev => ({
                    ...prev,
                    merchantProfile: { ...prev.merchantProfile, imagePreview: secureImage }
                }));
                localStorage.setItem('merchantLogo', secureImage);
            } catch (err) {
                console.error(err);
            }
        };

        fetchSecureLogo();
    }, [merchantProfile, setProfile]);

    const handleImageUpload = event => {
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];
            const imagePreview = URL.createObjectURL(file);

            setState(prev => ({
                ...prev,
                merchantProfile: {
                    ...prev.merchantProfile,
                    image: file,
                    imagePreview
                }
            }));
            saveLogoToLocalStorage(imagePreview);
        }
    };

    const validateProperty = event => {
        const { name, value } = event.target;
        const emailPattern = /^[^\s@]+@[^\s@]+$/;
        const pattern = /^[0-9]*$/;

        if (
            (name === 'businessEmail' && value.trim() !== '') ||
            (name === 'supportEmail' && value.trim() !== '') ||
            (name === 'chargebackEmail' && value.trim() !== '')
        ) {
            if (!emailPattern.test(value)) {
                return 'Please enter a valid email address';
            }
        } else if (name === 'businessPhone' && value.trim() !== '') {
            if (!pattern.test(value)) {
                return 'This field must contain numbers only!';
            }
        }
    };

    const handleChange = event => {
        const { name, value } = event.target;

        const merchantProfile = { ...state.merchantProfile };
        const location = merchantProfile['location'];

        if (name === 'countryId' && value) {
            dispatch(getState(value));

            location['countryId'] = value;

            return setState({ ...state, merchantProfile });
        }

        if (name === 'stateId' || name === 'streetAddress') {
            location[name] = value;
            return setState({ ...state, merchantProfile });
        }

        if (name === 'businessEmail' || name === 'businessPhone') {
            const errors = { ...state.errors };
            const errorMessage = validateProperty(event);

            if (errorMessage) {
                errors[name] = errorMessage;
            } else {
                delete errors[name];
            }

            const validate = { ...state.validate };
            validate[name] = value;

            return setState({ ...state, validate, errors });
        }
        merchantProfile[name] = value;
        setState({ ...state, merchantProfile });
    };

    const handleSubmit = event => {
        event.preventDefault();
        const { merchantProfile, validate } = state;
        const value = { ...merchantProfile, ...validate };

        dispatch(updateMerchantDetails(value));
        dispatch(setProcessLoading(true));
    };

    const uploadImage = () => {
        if (!state.merchantProfile.image) {
            alert('Please select an image first');
            return;
        }
        dispatch(
            uploadMerchantLogo({
                merchantId: state.merchantProfile.merchant_id,

                logoFile: state.merchantProfile.image
            })
        );
    };

    const {
        merchantProfile: {
            businessName,
            location: { stateId, streetAddress, countryId },
            businessTypeId,
            businessDescription
        },
        validate: { businessEmail, businessPhone },
        errors
    } = state;
    if (merchantProfile.id === undefined) return 'Loading';
    return (
        <div className="content-background__wrapper">
            <h2 className="settings__header u-half-gradient-bg">Business Profile</h2>

            <div className="img-upload">
                <div className="img-upload__display">
                    <DisplayImage image={state.merchantProfile.imagePreview} onImageUpload={handleImageUpload} />
                </div>
                <div className="img-upload__content">
                    <div className="img-upload__title  u-half-gradient-bg">Business logo</div>
                    <p className="img-upload__description">
                        We recommend you use a rectangle logo with dimensions 175px by 135px for best results on the
                        checkout form
                    </p>
                    <OiButton
                        onClick={uploadImage}
                        disabled={loading}
                        styles="oi-btn oi-btn--primary oi-btn--stretch oi-btn--radius5 oi-btn--height45 | u-font14sb"
                        type="submit"
                        buttonLoader={loading}>
                        Upload Logo
                    </OiButton>
                </div>
            </div>

            <div className="u-horizontal-line  u-margin-top-sm" />

            <div className="oi-settings__data">
                <form className="oi-form" onSubmit={handleSubmit}>
                    <div className="oi-form__section  u-margin-top-3_5">
                        <div className="oi-settings-section__title  u-half-gradient-bg">Business Details</div>

                        <div className="oi-form__group">
                            <div className="oi-form__model-direction">
                                <div className="oi-form__associate-1-of-2">
                                    <OiFormInput
                                        labelClass="oi-form__label-primary"
                                        inputClass="oi-form__input-primary oi-form__input--height4"
                                        name="businessName"
                                        type="text"
                                        label="Business Name"
                                        hook="business-name"
                                        onChange={handleChange}
                                        placeholder="Business Name"
                                        inputValue={businessName}
                                        required
                                    />
                                </div>

                                <div className="oi-form__associate-1-of-2  u-margin-top-switch">
                                    <div className={`oi-form__section`}>
                                        <div className="oi-form__group">
                                            <div className="oi-form__associate">
                                                <label className="oi-form__label-primary" htmlFor="business-type">
                                                    Business Category
                                                </label>
                                                <select
                                                    onChange={handleChange}
                                                    name="businessTypeId"
                                                    value={businessTypeId}
                                                    className="oi-form__select-primary oi-form__input--height4"
                                                    id="business-category">
                                                    {businessTypes.map((type, id) => (
                                                        <option key={id} value={type.id}>
                                                            {type.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="oi-form__group  u-margin-top-1-5">
                                <FormTextarea
                                    labelClass="oi-form__label-primary"
                                    inputClass="oi-form__textarea-primary"
                                    name="businessDescription"
                                    label="Business Description"
                                    hook="businessDescription"
                                    onChange={handleChange}
                                    placeholder="Tell us about your business..."
                                    inputValue={businessDescription}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="oi-form__section  u-margin-top-3_5">
                        <div className="oi-settings-section__title  u-half-gradient-bg">
                            Business Contact Information
                        </div>

                        <div className="oi-form__group">
                            <div className="oi-form__model-direction">
                                <div className="oi-form__associate-1-of-2">
                                    <OiFormInput
                                        labelClass="oi-form__label-primary"
                                        inputClass="oi-form__input-primary oi-form__input--height4"
                                        name="businessEmail"
                                        type="email"
                                        label="Contact Email"
                                        hook="businessEmail"
                                        inputValue={businessEmail}
                                        onChange={handleChange}
                                        placeholder="contact@email.com"
                                        error={errors['businessEmail']}
                                    />
                                </div>
                                <div className="oi-form__associate-1-of-2  u-margin-top-switch">
                                    <OiFormInput
                                        labelClass="oi-form__label-primary"
                                        inputClass="oi-form__input-primary oi-form__input--height4"
                                        name="businessPhone"
                                        type="text"
                                        label="Phone Number"
                                        hook="businessPhone"
                                        inputValue={businessPhone}
                                        onChange={handleChange}
                                        placeholder="Phone Number"
                                        error={errors['businessPhone']}
                                    />
                                </div>
                            </div>

                            <div className="oi-form__group">
                                <div className="oi-form__model-direction">
                                    <div className="oi-form__associate-1-of-2  u-margin-top-1-5">
                                        <OiFormInput
                                            labelClass="oi-form__label-primary"
                                            inputClass="oi-form__input-primary oi-form__input--height4"
                                            name="supportEmail"
                                            type="email"
                                            label="Support Email"
                                            hook="support-email"
                                            onChange={handleChange}
                                            placeholder="support@email.com"
                                            error={errors['supportEmail']}
                                        />
                                    </div>
                                    <div className="oi-form__associate-1-of-2  u-margin-top-1-5">
                                        <OiFormInput
                                            labelClass="oi-form__label-primary"
                                            inputClass="oi-form__input-primary oi-form__input--height4"
                                            name="chargebackEmail"
                                            type="email"
                                            label="Chargeback Email"
                                            hook="chargeback-email"
                                            onChange={handleChange}
                                            placeholder="emailtype@email.com"
                                            error={errors['chargebackEmail']}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="oi-form__section  u-margin-top-3_5">
                        <div className="oi-settings-section__title  u-half-gradient-bg">Address</div>

                        <div className="oi-form__group">
                            <div className="oi-form__model-direction">
                                <div className="oi-form__associate-1-of-2">
                                    <label
                                        className="oi-form__label-primary  oi-form__label-primary--mod"
                                        htmlFor="country">
                                        Country
                                    </label>
                                    <select
                                        onChange={handleChange}
                                        name="countryId"
                                        className="oi-form__select-primary oi-form__input--height4"
                                        id="countryId"
                                        value={countryId}>
                                        <option value="">...Select Country...</option>
                                        {country.map((type, id) => (
                                            <option key={id} value={type.id}>
                                                {type.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="oi-form__associate-1-of-2  u-margin-top-switch">
                                    <label
                                        className="oi-form__label-primary  oi-form__label-primary--mod"
                                        htmlFor="states">
                                        State
                                    </label>
                                    <select
                                        onChange={handleChange}
                                        name="stateId"
                                        className="oi-form__select-primary oi-form__input--height4"
                                        id="stateId"
                                        value={stateId}>
                                        <option value="">...Select State...</option>
                                        {allStates.map((type, id) => (
                                            <option key={id} value={type.id}>
                                                {type.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="oi-form__group  u-margin-top-1-5">
                                <FormTextarea
                                    labelClass="oi-form__label-primary"
                                    inputClass="oi-form__textarea-primary"
                                    name="streetAddress"
                                    label="Address"
                                    hook="streetAddress"
                                    onChange={handleChange}
                                    inputValue={streetAddress}
                                    placeholder="Where can we find you physically?"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="u-margin-top-3_5">
                        <OiButton
                            disabled={false}
                            styles="oi-btn oi-btn--primary oi-btn--stretch oi-btn--radius5 oi-btn--height45 | u-font14sb"
                            type="submit"
                            buttonLoader={loading}>
                            Submit
                        </OiButton>
                    </div>
                </form>
            </div>
        </div>
    );
};
export default BusinessProfile;
